/**
 * Non-secret AI Chat preferences: which provider and which model.
 *
 * localStorage on purpose — this is a UI preference like panel width, not a
 * secret. The API KEYS never come near this module: they live in the vault
 * (keyStore.js), and that split is the point of the whole BYO-key design.
 */

const STORAGE_KEY = 'aiChatSettings';

/** Fired on window whenever provider/model or a stored key changes. */
export const AI_SETTINGS_CHANGED_EVENT = 'ai-settings-changed';

/**
 * Provider catalogue. Each `models` list is ordered STRONGEST FIRST — the
 * first entry is the provider's default (see DEFAULT_MODELS), and the picker
 * renders them in this order.
 *
 * Verified against each vendor's own model docs on 17.08.2026. Treat that
 * date as the list's expiry: providers retire ids between our releases (see
 * RETIRED_MODELS), so re-check rather than assuming these are still live.
 */
export const AI_PROVIDERS = [
  {
    // The server-side assistant (22.08.2026): no key, the server picks the
    // model (OPENROUTER_MODEL on backend_phoenix — services/ai/apibooStatus.js
    // reports the real id). `models` holds one sentinel so every consumer that
    // iterates `models` keeps working; `keyless` is what they branch on.
    id: 'apiboo',
    label: 'Apiboo',
    models: ['apiboo'],
    keyless: true,
  },
  {
    id: 'anthropic',
    label: 'Anthropic',
    models: [
      'claude-opus-5',
      'claude-sonnet-5',
      'claude-opus-4-8',
      'claude-sonnet-4-6',
      'claude-haiku-4-5',
    ],
    keyHint: 'sk-ant-…',
    consoleUrl: 'https://console.anthropic.com/settings/keys',
  },
  {
    id: 'openai',
    label: 'OpenAI',
    // The 5.6 line is the current flagship trio (sol/terra/luna); 5.4 stays
    // for anyone pinned to it.
    models: ['gpt-5.6-sol', 'gpt-5.6-terra', 'gpt-5.6-luna', 'gpt-5.4'],
    keyHint: 'sk-…',
    consoleUrl: 'https://platform.openai.com/api-keys',
  },
  {
    id: 'gemini',
    // 17.08.2026: the 2.5 line is closed to new keys — Google answers
    // "models/gemini-2.5-flash is no longer available to new users" with a
    // 404 naming the replacement. Google retires ids faster than we ship, so
    // a stale entry here is a dead provider for anyone setting up today; the
    // custom-model field is the escape hatch when this list rots again.
    label: 'Google Gemini',
    models: ['gemini-3.7-flash', 'gemini-3.6-flash'],
    keyHint: 'AIza…',
    consoleUrl: 'https://aistudio.google.com/apikey',
  },
  {
    // 22.08.2026: a normal keyed provider — the user's own OpenRouter key,
    // straight to openrouter.ai (nothing goes through Apiboo's server). The
    // list is a short cut of the public catalogue (GET /api/v1/models, all
    // tool-capable on that date); the free-text model field covers the rest.
    id: 'openrouter',
    label: 'OpenRouter',
    models: [
      'openai/gpt-oss-120b',
      'anthropic/claude-sonnet-5',
      'openai/gpt-5.6-sol',
      'google/gemini-3.7-flash',
      'deepseek/deepseek-v3.2',
    ],
    keyHint: 'sk-or-v1-…',
    consoleUrl: 'https://openrouter.ai/settings/keys',
  },
];

/** Always the first entry of each provider's list — derived, not hand-kept. */
export const DEFAULT_MODELS = Object.fromEntries(
  AI_PROVIDERS.map((p) => [p.id, p.models[0]])
);

/**
 * Model ids the provider has since RETIRED, mapped to their replacement.
 *
 * Bumping AI_PROVIDERS alone does not help anyone who already ran the app:
 * their choice is in localStorage, and getAiSettings deliberately honours any
 * stored string so a hand-typed model keeps working. Without this map an
 * existing user keeps a dead id forever, the Settings dropdown shows the
 * healthy first entry while storage says otherwise, and every turn 404s.
 */
export const RETIRED_MODELS = {
  'gemini-2.5-pro': 'gemini-3.7-flash',
  'gemini-2.5-flash': 'gemini-3.6-flash',
  // Shipped as this app's OpenAI defaults before the 5.6 line landed.
  'gpt-5.2': 'gpt-5.6-sol',
  'gpt-5-mini': 'gpt-5.6-luna',
};

export function providerMeta(providerId) {
  return AI_PROVIDERS.find((p) => p.id === providerId) || AI_PROVIDERS[0];
}

/** Fresh installs start on the keyless assistant; a stored preference always wins. */
export const DEFAULT_PROVIDER = 'apiboo';

/** The raw stored object (or null) — provider/model plus housekeeping flags. */
function readStored() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return stored && typeof stored === 'object' ? stored : null;
  } catch {
    return null;
  }
}

function writeStored(obj) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    /* ignore quota/private-mode failures — next read falls back to defaults */
  }
}

/**
 * One-time default migration flag (22.08.2026, services/ai/apibooDefaultMigration.js).
 * Kept INSIDE the stored object rather than as its own key so a user who
 * later picks a BYO provider without a key on purpose is never migrated
 * again — the flag travels with the preference it guards.
 */
const MIGRATED_FLAG = 'apibooDefaultMigratedAt';

export function isApibooDefaultMigrated() {
  return Boolean(readStored()?.[MIGRATED_FLAG]);
}

export function markApibooDefaultMigrated() {
  writeStored({ ...(readStored() || {}), [MIGRATED_FLAG]: new Date().toISOString() });
}

/** { provider, model } — always valid, falling back to the Apiboo assistant. */
export function getAiSettings() {
  const stored = readStored();
  const provider = AI_PROVIDERS.some((p) => p.id === stored?.provider)
    ? stored.provider
    : DEFAULT_PROVIDER;
  // A free-text model is allowed (providers ship models faster than we ship
  // releases); only an empty one falls back to the per-provider default.
  const raw =
    typeof stored?.model === 'string' && stored.model.trim()
      ? stored.model.trim()
      : DEFAULT_MODELS[provider];
  // …but a KNOWN-DEAD id is not "custom", it's a stale preference. Migrate it
  // on read so an existing install heals itself without a settings visit.
  const model = RETIRED_MODELS[raw] || raw;
  return { provider, model };
}

export function setAiSettings(patch) {
  const current = getAiSettings();
  const next = { ...current, ...patch };
  // Switching provider invalidates the previous provider's model choice
  // unless the caller set one explicitly in the same patch.
  if (patch.provider && patch.provider !== current.provider && !patch.model) {
    next.model = DEFAULT_MODELS[patch.provider] || '';
  }
  // Housekeeping flags ride along; the returned shape stays { provider, model }.
  const migratedAt = readStored()?.[MIGRATED_FLAG];
  writeStored(migratedAt ? { ...next, [MIGRATED_FLAG]: migratedAt } : next);
  notifyAiSettingsChanged();
  return next;
}

/** Also called by keyStore after a key save/remove — same listeners care. */
export function notifyAiSettingsChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AI_SETTINGS_CHANGED_EVENT));
  }
}
